import React, { useCallback, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { NotificationManager } from '@atlas/core';
import { BasePopup, Button, ChipsItem, Icon, Input, Label, Loader } from '@atlas/ui';

import GlobalErrors from 'common/Form/GlobalErrors';
import useRealityCheckSettings from 'hooks/useRealityCheckSettings';

const UNIT_LABELS = {
  HOUR: 'reality_check_custom_hours',
  MINUTE: 'reality_check_custom_min',
};

const getOptionLabel = option => {
  if (option === 'CUSTOM') return <Label message="reality_check_custom_time" />;
  const [unit, value] = option.split('_');

  return (
    <span>
      {value} <Label message={UNIT_LABELS[unit] || unit.toLowerCase()} />
    </span>
  );
};

const PopupInput = ({ label, onBlur, onChange, value }) => {
  const [isActive, setIsActive] = useState(false);

  const onBlurHandler = useCallback(() => {
    setIsActive(false);
    onBlur?.();
  }, [onBlur]);

  const onFocusHandler = useCallback(() => {
    setIsActive(true);
  }, []);

  return (
    <Input
      active={isActive}
      label={label}
      onBlur={onBlurHandler}
      onChange={onChange}
      onFocus={onFocusHandler}
      value={value}
    />
  );
};

const RealityCheckTimeLimitsPopup = ({ data, id }) => {
  const {
    customHours,
    customMinutes,
    errors,
    initialized,
    isButtonDisabled,
    loading,
    onHoursChange,
    onMinutesBlur,
    onMinutesChange,
    onSaveHandler,
    selectedOption,
    setErrors,
    setSelectedOption,
    timeOptions,
  } = useRealityCheckSettings({
    onSaveSuccess: () => {
      NotificationManager.removeNotification(id);
      data?.onSuccessPopup();
    },
  });

  const onCloseHandler = useCallback(() => {
    data?.onClosePopup?.();
    NotificationManager.removeNotification(id);
  }, [data, id]);

  return (
    <BasePopup className="reality-check-time-limits-popup">
      <div className="popup__header">
        <Label className="popup__title" message="reality_check_popup_title" />
        <Label className="popup__description" message="reality_check_select_time" />
        <button className="close-button" onClick={onCloseHandler} type="button">
          <Icon height={24} name="close" width={24} />
        </button>
      </div>

      {!initialized ? (
        <Loader />
      ) : (
        <div className="popup__content">
          <Label className="content__label" message="reality_check_choose_time" />
          <div className="bullets">
            {timeOptions.map(option => {
              const onClickHandler = () => {
                setSelectedOption(option);
                setErrors([]);
              };

              return (
                <ChipsItem
                  active={selectedOption === option}
                  className="deposit-amounts-chip"
                  key={option}
                  label={getOptionLabel(option)}
                  onClick={onClickHandler}
                />
              );
            })}
          </div>

          {selectedOption === 'CUSTOM' && (
            <>
              <Label className="content__label" message="reality_check_custom_set" />
              <div
                className={cx('inputs', {
                  error: errors.length > 0,
                })}
              >
                <PopupInput label="Hours" onChange={onHoursChange} value={customHours} />
                <PopupInput
                  label="Minutes"
                  onBlur={onMinutesBlur}
                  onChange={onMinutesChange}
                  value={customMinutes}
                />
              </div>
              <GlobalErrors globalErrors={errors} />
            </>
          )}
        </div>
      )}

      <div className="popup__footer">
        <Button
          className="save-button"
          disabled={isButtonDisabled}
          label="save"
          loading={loading}
          onClick={onSaveHandler}
          size="big"
          type="primary"
        />
      </div>
    </BasePopup>
  );
};

PopupInput.propTypes = {
  label: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
};

RealityCheckTimeLimitsPopup.propTypes = {
  data: PropTypes.shape({
    onClosePopup: PropTypes.func,
    onSuccessPopup: PropTypes.func,
  }),
  id: PropTypes.string,
};

export default RealityCheckTimeLimitsPopup;
